import { motion, AnimatePresence } from "framer-motion";

const textWords = [
  { text: "VetAssist", gradient: false, blue: false },
  { text: "Clinic", gradient: true, blue: false },
  { text: "Software", gradient: false, blue: true },
];

// ── Animation variants ──────────────────────────────────────────────────────

const overlayVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { duration: 0.4, ease: "easeOut", staggerChildren: 0.1 },
  },
  exit: {
    opacity: 0,
    scale: 1.03,
    filter: "blur(12px)",
    transition: { duration: 0.5, ease: [0.4, 0, 1, 1] },
  },
};

const badgeVariants = {
  hidden: { opacity: 0, y: -8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut", delay: 0.1 } },
};

const lineVariants = {
  hidden: { scaleX: 0, originX: "center" },
  show: {
    scaleX: 1,
    transition: { duration: 0.6, ease: [0.19, 1, 0.22, 1], delay: 0.2 },
  },
};

const wordVariants = {
  hidden: { y: "120%", skewY: 6, opacity: 0, rotateX: 25 },
  show: {
    y: "0%",
    skewY: 0,
    opacity: 1,
    rotateX: 0,
    transition: { duration: 0.75, ease: [0.19, 1, 0.22, 1] },
  },
};

const taglineVariants = {
  hidden: { opacity: 0, letterSpacing: "0.6em" },
  show: {
    opacity: 1,
    letterSpacing: "0.25em",
    transition: { duration: 0.8, ease: "easeOut", delay: 0.55 },
  },
};

const pillVariants = {
  hidden: { scaleX: 0 },
  show: { scaleX: 1, transition: { duration: 1.2, ease: [0.19, 1, 0.22, 1], delay: 0.7 } },
};

const dotVariants = {
  hidden: { opacity: 0 },
  show: (i) => ({
    opacity: [0, 1, 0],
    transition: { duration: 1.5, delay: 0.9 + i * 0.18, repeat: Infinity, repeatDelay: 1.2 },
  }),
};

const orbVariants = {
  hidden: { opacity: 0, scale: 0.6 },
  show: (i) => ({
    opacity: [0, 0.45, 0.25],
    scale: [0.6, 1.15, 1],
    transition: { duration: 2.2, delay: i * 0.3, ease: "easeOut" },
  }),
};

// ── Sub-components ──────────────────────────────────────────────────────────

const DotGrid = () => (
  <svg
    className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.14]"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden>
    <defs>
      <pattern id="dots" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
        <circle cx="1" cy="1" r="1" fill="#1AAED4" />
      </pattern>
      <radialGradient id="dotFade" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="white" stopOpacity="0" />
        <stop offset="55%" stopColor="white" stopOpacity="0" />
        <stop offset="100%" stopColor="white" stopOpacity="1" />
      </radialGradient>
    </defs>
    <rect width="100%" height="100%" fill="url(#dots)" />
    <rect width="100%" height="100%" fill="url(#dotFade)" />
  </svg>
);

const GlowOrb = ({ style, custom }) => (
  <motion.div
    variants={orbVariants}
    custom={custom}
    className="pointer-events-none absolute rounded-full"
    style={style}
  />
);

// ✅ FIX: Added `lite` prop here so it can receive the value
const Word = ({ text, gradient, blue, lite }) => (
  <div className="overflow-hidden" style={{ perspective: "900px" }}>
    <motion.span
      variants={wordVariants}
      style={{ willChange: "transform, opacity", display: "block" }}>
      {gradient ? (
        <span
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 800,
            fontSize: "clamp(2.4rem, 7vw, 5rem)",
            background: "linear-gradient(135deg, #1AAED4 0%, #22C991 45%, #1AAED4 100%)",
            backgroundSize: "200% auto",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            // ✅ Stop infinite shimmer on mobile to save CPU
            animation: lite ? "none" : "shimmer 3.5s linear infinite",
            display: "inline-block",
          }}>
          {text}
        </span>
      ) : blue ? (
        /* ── "Software" — rich electric-blue ── */
        <span
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 800,
            fontSize: "clamp(2.4rem, 7vw, 5rem)",
            background: "linear-gradient(135deg, #0EA5E9 0%, #2563EB 50%, #1D4ED8 100%)",
            backgroundSize: "200% auto",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            // ✅ Stop infinite shimmer on mobile to save CPU
            animation: lite ? "none" : "shimmerBlue 3s linear infinite",
            display: "inline-block",
          }}>
          {text}
        </span>
      ) : (
        <span
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 700,
            fontSize: "clamp(2.4rem, 7vw, 5rem)",
            color: "#0B2A4A",
          }}>
          {text}
        </span>
      )}
    </motion.span>
  </div>
);

// ── Main component ──────────────────────────────────────────────────────────

// ✅ FIX: Destructure `lite` from props
const IntroTextOverlay = ({ visible, lite }) => {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          variants={overlayVariants}
          initial="hidden"
          animate="show"
          exit="exit"
          className="pointer-events-none fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden"
          style={{
            fontFamily: "'Poppins', sans-serif",
            willChange: "opacity, transform, filter",
          }}>

          {/* ── Keyframes + Poppins import ── */}
          <style>{`
            /* ── Animated aurora background ── */
            @keyframes auroraShift {
              0%   { background-position: 0% 50%; }
              50%  { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }

            /* Floating blob animations */
            @keyframes blobDrift1 {
              0%, 100% { transform: translate(0px, 0px) scale(1); }
              33%  { transform: translate(40px, -30px) scale(1.08); }
              66%  { transform: translate(-20px, 20px) scale(0.95); }
            }
            @keyframes blobDrift2 {
              0%, 100% { transform: translate(0px, 0px) scale(1); }
              33%  { transform: translate(-35px, 25px) scale(1.05); }
              66%  { transform: translate(25px, -18px) scale(0.97); }
            }
            @keyframes blobDrift3 {
              0%, 100% { transform: translate(0px, 0px) scale(1); }
              50%  { transform: translate(20px, 30px) scale(1.06); }
            }

            @keyframes shimmer {
              0%   { background-position: 0% center; }
              100% { background-position: 200% center; }
            }
            @keyframes shimmerBlue {
              0%   { background-position: 0% center; }
              100% { background-position: 200% center; }
            }

            /* Subtle ring pulse */
            @keyframes ringPulse {
              0%, 100% { opacity: 0.12; transform: scale(1); }
              50%       { opacity: 0.22; transform: scale(1.06); }
            }
          `}</style>

          {/* ══════════════════════════════════════════
              ANIMATED BACKGROUND LAYERS
          ══════════════════════════════════════════ */}

          {/* Base gradient — animated mesh */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(-45deg, #E0F4FF, #D6F5EC, #EEF9FF, #C8EAF9, #D0F5EB, #E8F8FF)",
              // ✅ Gated by `lite`
              backgroundSize: lite ? "100% 100%" : "400% 400%",
              animation: lite ? "none" : "auroraShift 14s ease infinite",
            }}
          />

          {/* Floating color blob 1 — teal/cyan */}
          {/* ✅ Rendered only if NOT lite */}
          {!lite && (
            <div
              className="pointer-events-none absolute rounded-full"
              style={{
                width: 700,
                height: 700,
                top: "-15%",
                left: "10%",
                background:
                  "radial-gradient(circle at 40% 40%, rgba(26,174,212,0.28) 0%, rgba(34,201,145,0.14) 50%, transparent 70%)",
                animation: "blobDrift1 12s ease-in-out infinite",
                filter: "blur(2px)",
              }}
            />
          )}

          {/* Floating color blob 2 — deep blue */}
          {!lite && (
            <div
              className="pointer-events-none absolute rounded-full"
              style={{
                width: 580,
                height: 580,
                bottom: "-10%",
                right: "5%",
                background:
                  "radial-gradient(circle at 60% 60%, rgba(37,99,235,0.18) 0%, rgba(14,165,233,0.12) 45%, transparent 68%)",
                animation: "blobDrift2 15s ease-in-out infinite",
                filter: "blur(2px)",
              }}
            />
          )}

          {/* Floating color blob 3 — mint green */}
          {!lite && (
            <div
              className="pointer-events-none absolute rounded-full"
              style={{
                width: 420,
                height: 420,
                top: "40%",
                left: "-5%",
                background: "radial-gradient(circle, rgba(34,201,145,0.18) 0%, transparent 65%)",
                animation: "blobDrift3 10s ease-in-out infinite",
                filter: "blur(1px)",
              }}
            />
          )}

          {/* Subtle decorative ring */}
          {!lite && (
            <>
              <div
                className="pointer-events-none absolute rounded-full"
                style={{
                  width: 900,
                  height: 900,
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  border: "1px solid rgba(26,174,212,0.15)",
                  animation: "ringPulse 6s ease-in-out infinite",
                }}
              />
              <div
                className="pointer-events-none absolute rounded-full"
                style={{
                  width: 600,
                  height: 600,
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  border: "1px solid rgba(34,201,145,0.12)",
                  animation: "ringPulse 6s ease-in-out infinite 1.5s",
                }}
              />
            </>
          )}

          {/* ── Background dot grid ── */}
          <DotGrid />

          {/* ── Ambient glow orbs (motion) ── */}
          {!lite && (
            <>
              <GlowOrb
                custom={0}
                style={{
                  width: 480,
                  height: 480,
                  top: "-10%",
                  left: "20%",
                  background: "radial-gradient(circle, rgba(26,174,212,0.25) 0%, transparent 70%)",
                }}
              />
              <GlowOrb
                custom={1}
                style={{
                  width: 360,
                  height: 360,
                  bottom: "5%",
                  right: "10%",
                  background: "radial-gradient(circle, rgba(34,201,145,0.22) 0%, transparent 70%)",
                }}
              />
              <GlowOrb
                custom={2}
                style={{
                  width: 280,
                  height: 280,
                  top: "30%",
                  left: "5%",
                  background: "radial-gradient(circle, rgba(37,99,235,0.16) 0%, transparent 70%)",
                }}
              />
            </>
          )}

          {/* ══════════════════════════════════════════
              CONTENT
          ══════════════════════════════════════════ */}

          {/* ── Top badge ── */}
          <motion.div
            variants={badgeVariants}
            className="relative mb-10 flex items-center gap-5 px-4">
            <motion.span
              variants={lineVariants}
              className="hidden h-px w-20 sm:block"
              style={{ background: "linear-gradient(90deg, transparent, #1AAED4)" }}
            />
            <span
              className="inline-flex items-center gap-2 rounded-full border px-5 py-2"
              style={{
                borderColor: "rgba(26,174,212,0.35)",
                // ✅ Backdrop fix gated by `lite`
                backgroundColor: lite ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.55)",
                backdropFilter: lite ? "none" : "blur(8px)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
              }}>
              <span
                className="h-2 w-2 rounded-full"
                style={{
                  backgroundColor: "#22C991",
                }}
              />
              <span
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  letterSpacing: "0.3em",
                  textTransform: "uppercase",
                  color: "#1AAED4",
                }}>
                Clinic First
              </span>
            </span>
            <motion.span
              variants={lineVariants}
              className="hidden h-px w-20 sm:block"
              style={{ background: "linear-gradient(90deg, #22C991, transparent)" }}
            />
          </motion.div>

          {/* ── Main headline ── */}
          <div className="w-full px-6">
            <motion.div
              variants={{ show: { transition: { staggerChildren: 0.09, delayChildren: 0.2 } } }}
              className="mx-auto flex max-w-[95vw] flex-wrap items-baseline justify-center gap-x-6 gap-y-2 text-center">
              {textWords.map(({ text, gradient, blue }, i) => (
                // ✅ Fixed: Passing `lite` down to the Word component here!
                <Word key={i} text={text} gradient={gradient} blue={blue} lite={lite} />
              ))}
            </motion.div>
          </div>

          {/* ── Tagline ── */}
          <motion.p
            variants={taglineVariants}
            style={{
              marginTop: "2rem",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500,
              fontSize: "0.72rem",
              textTransform: "uppercase",
              letterSpacing: "0.25em",
              color: "#3A9AB5",
              willChange: "opacity, letter-spacing",
            }}>
            Secure&nbsp;·&nbsp;Scale&nbsp;·&nbsp;Accelerate
          </motion.p>

          {/* ── Animated dots ── */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.85 }}
            style={{ marginTop: "2rem", display: "flex", alignItems: "center", gap: "0.75rem" }}>
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                variants={dotVariants}
                custom={i}
                style={{
                  display: "block",
                  height: 8,
                  width: 8,
                  borderRadius: "9999px",
                  backgroundColor: i === 1 ? "#22C991" : i === 2 ? "#2563EB" : "#1AAED4",
                  opacity: 0,
                }}
              />
            ))}
          </motion.div>

          {/* ── Bottom vertical line ── */}
          <motion.div
            variants={pillVariants}
            style={{ marginTop: "2.5rem", height: 60, width: 1, transformOrigin: "top" }}>
            <div
              style={{
                height: "100%",
                width: "100%",
                background: "linear-gradient(to bottom, #2563EB, #1AAED4, #22C991, transparent)",
              }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default IntroTextOverlay;